import {
  Box,
  Button,
  CheckboxField,
  CurrencyField,
  DateField,
  GooglePlacesAutocompleteField,
  HStack,
  SingleSelectField,
  ToggleButtonGroupField,
  useAnalytics,
  VStack,
} from '@medely/ui-kit';
import React from 'react';
import config from '@mp/config';
import { EMPLOYMENT_TYPE_OPTIONS, PATIENT_CARE_OPTIONS } from '../constants';
import { SetPageProps } from '../interface';
import { FragmentType, parseFragmentData } from '../../../../../graphql/generated';
import { useWorkExperience } from '../hooks/useWorkExperience';
import { useWageLog } from '../hooks/useWageLog';
import isNil from 'lodash/isNil';
import { useCurrentUser, useGate } from '@mp/hooks';
import { gateNames } from '@mp/constants';
import { useLocationOptionsForWorkExperience, WorkExperienceFormCurrentUserGql } from '../graphql';
import { useWatch } from '@medely/ui-kit/react-hook-form';

type WorkExperienceFormProps = {
  currentUser?: FragmentType<typeof WorkExperienceFormCurrentUserGql>;
  hidePositionField?: boolean;
  positionId?: number;
  hideButtonToSummary?: boolean;
  isExtraInformation?: boolean;
  setPage?: (value: SetPageProps) => void;
  showWageLog?: boolean;
  onValidationChange?: (canContinue: boolean) => void;
};

export const WorkExperienceForm = ({
  currentUser: inputCurrentUser,
  hidePositionField = false,
  positionId,
  hideButtonToSummary = false,
  isExtraInformation = false,
  setPage,
  showWageLog = false,
}: WorkExperienceFormProps) => {
  const { proType } = useCurrentUser();
  const { locationTypeOptions } = useLocationOptionsForWorkExperience();
  const facilityInfoEnabled = useGate(gateNames.facilityInfoInWh);
  const currentUser = parseFragmentData(WorkExperienceFormCurrentUserGql, inputCurrentUser);
  const analytics = useAnalytics();
  // @ts-ignore
  const { positionsOptions } = useWorkExperience({ currentUser });
  const selectedPositionId = useWatch({ name: 'position_id' });
  const currentlyWorkHere = useWatch({ name: 'currently_work_here' });

  const parsedPositionId = positionId ?? selectedPositionId;
  const zipCode = currentUser?.address_zip;

  const getMarketIdFromZip = () => {
    const allMarkets = currentUser?.professional?.markets ?? [];
    const marketsWithZip =
      allMarkets.filter((market) =>
        market.market_locations?.find((market_location) => market_location.zip_code === zipCode),
      ) ?? [];

    return !!marketsWithZip.length
      ? marketsWithZip[0].id
      : currentUser?.professional?.markets?.[0].id;
  };

  const { hasProfessionalRatesData, wageLogHelperText } = useWageLog({
    market_id: getMarketIdFromZip(),
    position_id: parsedPositionId,
  });

  const selectedPositionData = currentUser?.professional?.professional_positions?.find(
    (p: { position: { id: number } }) => p.position.id === parsedPositionId,
  );

  const selectedPositionDataYoE = selectedPositionData?.years_of_experience;
  const startDateHelperText =
    !isNil(selectedPositionDataYoE) && selectedPositionDataYoE <= 1
      ? 'Exclude new hire training period'
      : undefined;

  const showWageLogField = isExtraInformation || hasProfessionalRatesData || showWageLog;

  const handleGoToSummaryClick = () => {
    analytics.track(
      'Product Application - Primary Experience Required Experience Button - Clicked',
      {
        account_id: currentUser?.id,
        context: 'Product Application Primary Experience',
        subcontext: 'Required Experience Button',
        action: 'Clicked',
        pro_type: proType,
      },
    );
    if (setPage) {
      setPage('experienceSummary');
    }
  };

  return (
    <>
      {!hidePositionField && (
        <>
          <Box testId="work-experience-form-position-id">
            <SingleSelectField
              label={isExtraInformation ? 'Position' : 'Most recent position'}
              name="position_id"
              options={positionsOptions}
              placeholder="Select position"
            />
          </Box>

          <Box py={1} />
        </>
      )}

      {(selectedPositionId || hidePositionField) && (
        <>
          <Box testId="work-experience-form-bedside-care">
            <ToggleButtonGroupField
              label="Did you provide direct patient care?"
              name="bedside_care"
              options={PATIENT_CARE_OPTIONS}
            />
          </Box>
          <Box py={1} />
          <Box testId="work-experience-form-location-type-id">
            {facilityInfoEnabled ? (
              <GooglePlacesAutocompleteField
                googlePlacesKey={config.googleMapsKey}
                label="Facility"
                name="organization_name"
                placeIdName="google_place_id"
                type="name"
              />
            ) : (
              <SingleSelectField
                label="Facility type"
                name="location_type_id"
                options={locationTypeOptions}
                placeholder=""
              />
            )}
          </Box>

          <Box py={1} />
          {showWageLogField && (
            <>
              <Box testId="work-experience-form-hourly-rate-cents">
                <CurrencyField
                  name="hourly_rate_cents"
                  label="Hourly rate (optional)"
                  helperText={wageLogHelperText}
                />
              </Box>
              <Box py={1} />
            </>
          )}
          <Box testId="work-experience-form-employment-type">
            <SingleSelectField
              label="Employment type"
              name="employment_type"
              options={EMPLOYMENT_TYPE_OPTIONS}
              placeholder=""
            />
          </Box>
          <Box py={1} />
          <Box testId="work-experience-form-currently-work-here">
            <CheckboxField
              label="I’m currently working in this position"
              name="currently_work_here"
            />
          </Box>
          <HStack gap={0.5}>
            <VStack
              mr={currentlyWorkHere ? 0 : 1}
              width={currentlyWorkHere ? '100%' : '48%'}
              testId="work-experience-form-start-date"
            >
              <DateField
                name="start_date"
                label="Start date"
                helperText={startDateHelperText}
                shouldDisableDate={(day) => day.toJSDate() > new Date()}
              />
            </VStack>
            {!currentlyWorkHere && (
              <VStack mr={1} width="49%" testId="work-experience-form-end-date">
                <DateField
                  name="end_date"
                  label="End date"
                  shouldDisableDate={(day) => day.toJSDate() > new Date()}
                />
              </VStack>
            )}
          </HStack>
          <Box py={1} />
          {!hideButtonToSummary && (
            <HStack py={1} justifyContent="center" alignItems="center">
              <Button
                variant="text"
                onClick={() => handleGoToSummaryClick()}
                testId="work-experience-form-go-to-summary-button"
              >
                I don’t have the required experience
              </Button>
            </HStack>
          )}
          <Box py={1} />
        </>
      )}
    </>
  );
};
